import useMediaQuery from "../hooks/useMediaQuery";
import usePdfPreview from "../hooks/usePdfPreview";
import useRecoverableDraft from "../hooks/useRecoverableDraft";
import LoadError from "../components/LoadError";
import { Link, useSearchParams } from "react-router-dom";
import Dialog from "../components/Dialog";
import { discardChanges, useConfirm } from "../components/ConfirmProvider";
import useUnsavedChanges from "../hooks/useUnsavedChanges";
import useActionLock from "../hooks/useActionLock";
import { formatMoney } from "../utils/format";
import BillingSummary from "../components/BillingSummary";
import SourceBillingConditions from "../components/SourceBillingConditions";
import DevisSignature, { signatureLabel } from "../components/DevisSignature";
import {
  Archive,
  Copy,
  Download,
  Edit3,
  Eye,
  FileText,
  FileJson,
  Mail,
  Plus,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";

import { clientsAPI, devisAPI, parametresAPI } from "../services/api";
import toast from "react-hot-toast";

const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const extractFilename = (headers, fallback) => {
  const disposition = headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
};

const triggerBlobDownload = (response, fallbackFilename) => {
  const blob = new Blob([response.data], {
    type: response.headers?.["content-type"] || "application/octet-stream",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = extractFilename(response.headers, fallbackFilename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const toPrettyJson = (value) => JSON.stringify(value, null, 2);

const sanitizeJsonInput = (value) => {
  const normalized = String(value || "").replace(/^\uFEFF/, "").trim();

  const fenced = normalized.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (fenced) {
    return fenced[1].trim();
  }

  return normalized;
};

const formatValidationPath = (loc = []) => {
  const filtered = (Array.isArray(loc) ? loc : []).filter(
    (part) => part !== "body" && part !== "query" && part !== "path"
  );

  return filtered
    .map((part) => (typeof part === "number" ? `[${part}]` : String(part)))
    .reduce((acc, part) => {
      if (part.startsWith("[")) return `${acc}${part}`;
      return acc ? `${acc}.${part}` : part;
    }, "");
};

const formatDetailItem = (item) => {
  const path = formatValidationPath(item?.loc);
  const type = item?.type || "";
  const message = item?.msg || "Valeur invalide";

  if (type === "extra_forbidden") {
    return `Champ inconnu : ${path}`;
  }
  if (type === "missing") {
    return `Champ requis manquant : ${path}`;
  }
  if (type.startsWith("string_too_short")) {
    return `Champ texte vide ou trop court : ${path}`;
  }
  if (type.startsWith("greater_than")) {
    return `Valeur trop petite pour : ${path}`;
  }
  if (type.startsWith("greater_than_equal")) {
    return `Valeur invalide pour : ${path}`;
  }
  if (type.includes("float_parsing") || type.includes("int_parsing")) {
    return `Type invalide pour : ${path}`;
  }
  if (type.includes("model_type") || type.includes("list_type")) {
    return `Structure invalide pour : ${path}`;
  }

  return path ? `${path} : ${message}` : message;
};

const formatApiError = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail.map(formatDetailItem).filter(Boolean);
    return messages.slice(0, 5).join("\n");
  }

  return fallback;
};

const extractApiErrors = (error, fallback) => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return [detail];
  }

  if (Array.isArray(detail) && detail.length > 0) {
    const messages = detail.map(formatDetailItem).filter(Boolean);
    return messages.slice(0, 8);
  }

  return [fallback];
};

const createDevisLine = () => ({
  description: "",
  quantite: 1,
  unite: "forfait",
  price_type: "fixed",
  prix_unitaire: 0,
  prix_libre: "",
});

const createOption = () => ({
  description: "",
  type_element: "",
  tarif: "",
});

const createTotalIndicatif = () => ({
  libelle: "",
  montant: "",
});

const createAbonnement = () => ({
  description: "",
  tarif: "",
});

const createCondition = () => ({
  titre: "",
  contenu: "",
});

const ABONNEMENT_PRESETS = [
  {
    label: "Maintenance",
    items: [
      {
        description:
          "Maintenance technique (mises a jour, securite, sauvegardes, corrections mineures, support de base) - contrat annuel reconductible",
        tarif: "100 EUR HT / mois",
      },
    ],
  },
  {
    label: "Resend",
    items: [
      {
        description:
          "Cout Resend (emails transactionnels & newsletter) - refacture au reel sur justificatif. Plan Free : 0 EUR jusqu'a 3 000 emails/mois et 1 000 contacts. Plan Pro Marketing : ~38 EUR/mois jusqu'a 5 000 contacts, ~75 EUR/mois jusqu'a 10 000 contacts.",
        tarif: "0 - 75 EUR HT / mois (au reel)",
      },
    ],
  },
  {
    label: "Maintenance + Resend",
    items: [
      {
        description:
          "Maintenance technique (mises a jour, securite, sauvegardes, corrections mineures, support de base) - contrat annuel reconductible",
        tarif: "100 EUR HT / mois",
      },
      {
        description:
          "Cout Resend (emails transactionnels & newsletter) - refacture au reel sur justificatif. Plan Free : 0 EUR jusqu'a 3 000 emails/mois et 1 000 contacts. Plan Pro Marketing : ~38 EUR/mois jusqu'a 5 000 contacts, ~75 EUR/mois jusqu'a 10 000 contacts.",
        tarif: "0 - 75 EUR HT / mois (au reel)",
      },
    ],
  },
];

const CONDITION_PRESETS = [
  {
    label: "Delais",
    items: [
      {
        titre: "Delais",
        contenu:
          "1 a 3 semaines apres validation du devis et reception des contenus.",
      },
    ],
  },
  {
    label: "Paiement",
    items: [
      {
        titre: "Paiement",
        contenu:
          "30 % a la validation du devis, 40 % a la validation fonctionnelle, 30 % a la mise en ligne.",
      },
    ],
  },
  {
    label: "Avis Google",
    items: [
      {
        titre: "Avis Google",
        contenu:
          "Merci de confirmer le choix retenu : Option A (hardcodes, recommandee) ou Option B (API Google - contraintes decrites ci-dessus).",
      },
    ],
  },
  {
    label: "Contenus",
    items: [
      {
        titre: "Contenus",
        contenu:
          "Les textes, photos et contenus sont fournis par le client. Les delais peuvent varier selon la reception des elements.",
      },
    ],
  },
  {
    label: "Propriete",
    items: [
      {
        titre: "Propriete",
        contenu:
          "Le client beneficie d'un droit d'utilisation du site pour son activite.",
      },
    ],
  },
  {
    label: "Pack site web",
    items: [
      {
        titre: "Delais",
        contenu:
          "1 a 3 semaines apres validation du devis et reception des contenus.",
      },
      {
        titre: "Paiement",
        contenu:
          "30 % a la validation du devis, 40 % a la validation fonctionnelle, 30 % a la mise en ligne.",
      },
      {
        titre: "Avis Google",
        contenu:
          "Merci de confirmer le choix retenu : Option A (hardcodes, recommandee) ou Option B (API Google - contraintes decrites ci-dessus).",
      },
      {
        titre: "Contenus",
        contenu:
          "Les textes, photos et contenus sont fournis par le client. Les delais peuvent varier selon la reception des elements.",
      },
      {
        titre: "Propriete",
        contenu:
          "Le client beneficie d'un droit d'utilisation du site pour son activite.",
      },
    ],
  },
];

const createManualDraft = () => ({
  meta: {
    titre: "DEVIS",
    objet: "",
    reference: "",
    validite_jours: 30,
  },
  contexte: "",
  prestations_retenues: [createDevisLine()],
  options_variantes: [],
  totaux_indicatifs: [],
  abonnement_mensuel: [],
  conditions: [],
  notes: "",
});

const createDefaultFacturationPlan = () => [
  { id: "palier-1", label: "Acompte", pourcentage: 30 },
  { id: "palier-2", label: "Intermédiaire", pourcentage: 40 },
  { id: "palier-3", label: "Solde", pourcentage: 30 },
];

const cleanText = (value) => String(value || "").trim();

const parsePositiveNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatCurrency = formatMoney;

const formatPercentage = (value) => `${Number(value || 0).toFixed(2)} %`;

const normalizeFacturationRows = (item) => {
  const plan = item?.facturation_plan;
  const rows = Array.isArray(plan) && plan.length > 0 ? plan : createDefaultFacturationPlan();

  return rows.map((row, index) => ({
    id: row.id || `palier-${index + 1}`,
    label: row.label || `Palier ${index + 1}`,
    pourcentage: row.pourcentage ?? 0,
    facture_id: row.facture_id || null,
    facture_numero: row.facture_numero || null,
    facture_statut: row.facture_statut || null,
    montant_ht: row.montant_ht,
  }));
};

const hasAnyValue = (item) =>
  Object.values(item || {}).some((value) => cleanText(value).length > 0);

const withoutComputedFields = (content) => {
  const next = { ...(content || {}) };
  delete next.total_ht;
  if (next.meta) {
    next.meta = { ...next.meta };
    delete next.meta.date_document;
  }
  return next;
};

const contentToDraft = (content) => {
  const normalized = withoutComputedFields(content || {});

  return {
    meta: {
      titre: normalized.meta?.titre || "DEVIS",
      objet: normalized.meta?.objet || "",
      reference: normalized.meta?.reference || "",
      validite_jours: normalized.meta?.validite_jours || 30,
    },
    contexte: normalized.contexte || "",
    prestations_retenues:
      normalized.prestations_retenues?.length > 0
        ? normalized.prestations_retenues.map((line) => ({
            description: line.description || "",
            quantite: line.quantite ?? 1,
            unite: line.unite || "forfait",
            price_type: line.prix_unitaire == null ? "free" : "fixed",
            prix_unitaire: line.prix_unitaire ?? 0,
            prix_libre: line.prix_libre || "",
          }))
        : [createDevisLine()],
    options_variantes: normalized.options_variantes || [],
    totaux_indicatifs: normalized.totaux_indicatifs || [],
    abonnement_mensuel: normalized.abonnement_mensuel || [],
    conditions: normalized.conditions || [],
    notes: normalized.notes || "",
  };
};

const buildContentFromDraft = (draft) => {
  const optionalOptions = (draft.options_variantes || []).filter(hasAnyValue);
  const optionalTotals = (draft.totaux_indicatifs || []).filter(hasAnyValue);
  const optionalSubscriptions = (draft.abonnement_mensuel || []).filter(hasAnyValue);
  const optionalConditions = (draft.conditions || []).filter(hasAnyValue);

  return {
    meta: {
      titre: cleanText(draft.meta.titre) || "DEVIS",
      objet: cleanText(draft.meta.objet),
      reference: cleanText(draft.meta.reference) || null,
      validite_jours: Math.max(
        1,
        Math.round(parsePositiveNumber(draft.meta.validite_jours, 30))
      ),
    },
    contexte: cleanText(draft.contexte),
    prestations_retenues: (draft.prestations_retenues || []).map((line) => {
      const isFreePrice = line.price_type === "free";
      return {
        description: cleanText(line.description),
        quantite: parsePositiveNumber(line.quantite, 0),
        unite: cleanText(line.unite) || "forfait",
        prix_unitaire: isFreePrice
          ? null
          : Math.max(0, parsePositiveNumber(line.prix_unitaire, 0)),
        prix_libre: isFreePrice ? cleanText(line.prix_libre) || null : null,
      };
    }),
    options_variantes: optionalOptions.map((item) => ({
      description: cleanText(item.description),
      type_element: cleanText(item.type_element),
      tarif: cleanText(item.tarif),
    })),
    totaux_indicatifs: optionalTotals.map((item) => ({
      libelle: cleanText(item.libelle),
      montant: cleanText(item.montant),
    })),
    abonnement_mensuel: optionalSubscriptions.map((item) => ({
      description: cleanText(item.description),
      tarif: cleanText(item.tarif),
    })),
    conditions: optionalConditions.map((item) => ({
      titre: cleanText(item.titre),
      contenu: cleanText(item.contenu),
    })),
    notes: cleanText(draft.notes) || null,
  };
};

const hasLinkedInvoices = (item) => Boolean(
  item?.has_linked_invoices ||
  item?.facturation_plan?.some((row) => row.facture_id) ||
  ["partiel", "facture"].includes(item?.facturation_statut)
);

const contentLockReason = (item) => item?.statut === "archive"
  ? "Restaurez le devis avant de le modifier."
  : hasLinkedInvoices(item)
    ? "Ce devis est lié à des factures. Son contenu et son total sont figés."
    : "";

const calculateDraftTotal = (draft) =>
  (draft.prestations_retenues || []).reduce((sum, line) => {
    if (line.price_type === "free") return sum;
    return (
      sum +
      parsePositiveNumber(line.quantite, 0) *
        parsePositiveNumber(line.prix_unitaire, 0)
    );
  }, 0);

const FacturationPlanModal = ({
  item,
  rows,
  mutatingId,
  onChangeRows,
  onClose,
  onSavePlan,
  onInvoicePalier,
}) => {
  const [conditionsReady, setConditionsReady] = useState(false);
  const hasSavedPlan = Array.isArray(item?.facturation_plan) && item.facturation_plan.length > 0;
  const hasInvoicedPalier = rows.some(
    (row) => row.facture_id && row.facture_statut !== "annulee"
  );
  const savedPlan = Array.isArray(item?.facturation_plan) ? item.facturation_plan : [];
  const planIsDirty =
    savedPlan.length !== rows.length ||
    rows.some((row, index) => {
      const saved = savedPlan[index];
      if (!saved) return true;
      return (
        cleanText(saved.label) !== cleanText(row.label) ||
        Math.abs(
          parsePositiveNumber(saved.pourcentage, 0) -
            parsePositiveNumber(row.pourcentage, 0)
        ) > 0.01
      );
    });
  const confirm = useConfirm();
  useUnsavedChanges(planIsDirty);
  const close = async () => {
    if (mutatingId) return;
    if (!planIsDirty || await confirm(discardChanges)) onClose();
  };
  const totalPercentage = rows.reduce(
    (sum, row) => sum + parsePositiveNumber(row.pourcentage, 0),
    0
  );
  const percentageIsValid = Math.abs(totalPercentage - 100) <= 0.01;
  const lockedPercentage = rows.reduce(
    (sum, row) =>
      row.facture_id && row.facture_statut !== "annulee"
        ? sum + parsePositiveNumber(row.pourcentage, 0)
        : sum,
    0
  );
  const editablePercentage = totalPercentage - lockedPercentage;
  const totalHt = Number(item?.total_ht || 0);
  const devise = item?.devise || "EUR";
  const archived = item?.statut === "archive";

  const updateRow = (index, field, value) => {
    onChangeRows(
      rows.map((row, currentIndex) =>
        currentIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const addRow = () => {
    const usedIds = new Set(rows.map((row) => row.id));
    let index = rows.length + 1;
    while (usedIds.has(`palier-${index}`)) index += 1;
    onChangeRows([
      ...rows,
      {
        id: `palier-${index}`,
        label: `Palier ${index}`,
        pourcentage: 0,
      },
    ]);
  };

  const removeRow = (index) => {
    if (rows.length <= 1) return;
    onChangeRows(rows.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <Dialog title={`${archived ? "Échéancier du devis" : "Facturer le devis"} ${item?.numero}`} onClose={close} busy={!!mutatingId} size="max-w-4xl">
        <div className="space-y-4">
          {archived ? <p role="status" className="text-sm text-gray-600">Ce devis est archivé. Ses factures sont conservées. Restaurez-le pour facturer les paliers restants ou modifier l'échéancier.</p> : <SourceBillingConditions kind="devis" sourceId={item.id} onStatus={setConditionsReady} />}
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs font-medium uppercase text-gray-500">Total HT</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatCurrency(totalHt, devise)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs font-medium uppercase text-gray-500">Déjà facturé</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatCurrency(item?.montant_facture, devise)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-xs font-medium uppercase text-gray-500">Reste</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {formatCurrency(item?.reste_a_facturer ?? totalHt, devise)}
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[760px] divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Palier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    %
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Montant
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Facture
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {rows.map((row, index) => {
                  const hasActiveFacture =
                    Boolean(row.facture_id) && row.facture_statut !== "annulee";
                  const locked = archived || hasActiveFacture;
                  const palierAmount =
                    row.montant_ht ??
                    (totalHt * parsePositiveNumber(row.pourcentage, 0)) / 100;
                  return (
                    <tr key={`${row.id}-${index}`}>
                      <td className="px-4 py-4">
                        <input
                          type="text"
                          className="input-field"
                          aria-label={`Libellé du palier ${index + 1}`}
                          value={row.label}
                          disabled={locked}
                          onChange={(event) =>
                            updateRow(index, "label", event.target.value)
                          }
                        />
                      </td>
                      <td className="px-4 py-4">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          className="input-field"
                          aria-label={`Pourcentage du palier ${index + 1}`}
                          value={row.pourcentage}
                          disabled={locked}
                          onChange={(event) =>
                            updateRow(index, "pourcentage", event.target.value)
                          }
                        />
                      </td>
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {formatCurrency(palierAmount, devise)}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        {row.facture_id ? (
                          <div>
                            <div className="font-medium text-gray-900">
                              <Link className="text-primary-700 hover:underline" to={`/factures?fiche=${row.facture_id}`}>{row.facture_numero}</Link>
                            </div>
                            <div className="text-xs text-gray-500">
                              {row.facture_statut === "paye"
                                ? "Payée"
                                : row.facture_statut === "annulee"
                                ? "Annulée"
                                : "En attente"}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500">À facturer</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {hasActiveFacture ? (
                          <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">
                            Créée
                          </span>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            {hasSavedPlan && (
                              <button
                                type="button"
                                onClick={() => onInvoicePalier(row)}
                                disabled={
                                  archived ||
                                  planIsDirty ||
                                  !conditionsReady ||
                                  mutatingId === `invoice-${item.id}-${row.id}`
                                }
                                title={
                                  planIsDirty
                                    ? "Enregistrez l'échéancier avant de facturer ce palier"
                                    : undefined
                                }
                                className="btn-primary disabled:opacity-40"
                              >
                                <FileText className="h-4 w-4" />
                                Facturer
                              </button>
                            )}
                            <button
                              type="button"
                              aria-label={`Supprimer le palier ${index + 1}`}
                        onClick={() => removeRow(index)}
                              disabled={archived || rows.length <= 1}
                              className="rounded p-2 text-red-600 hover:bg-red-50 hover:text-red-900 disabled:opacity-40"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <button type="button" onClick={addRow} className="btn-secondary" disabled={archived}>
            <Plus className="h-4 w-4" />
            Ajouter un palier
          </button>

          <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p
                className={`text-sm font-medium ${
                  percentageIsValid ? "text-gray-600" : "text-red-600"
                }`}
              >
                Total des paliers : {formatPercentage(totalPercentage)}
              </p>
              {hasInvoicedPalier && (
                <p className="mt-1 text-xs text-gray-500">
                  Déjà facturé : {formatPercentage(lockedPercentage)} - reste à
                  répartir : {formatPercentage(editablePercentage)} /{" "}
                  {formatPercentage(100 - lockedPercentage)}
                </p>
              )}
            </div>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={close} className="btn-secondary">
                Fermer
              </button>
              <button
                type="button"
                onClick={onSavePlan}
                disabled={archived || !percentageIsValid || !planIsDirty || mutatingId === `plan-${item.id}`}
                className="btn-primary"
              >
                <Save className="h-4 w-4" />
                {hasSavedPlan ? "Mettre à jour l'échéancier" : "Enregistrer l'échéancier"}
              </button>
            </div>
          </div>
        </div>
    </Dialog>
  );
};

const Devis = () => {
  const desktop = useMediaQuery("(min-width: 640px)");
  const { openPDF, preview } = usePdfPreview();
  const [params, setParams] = useSearchParams();
  const [loadError, setLoadError] = useState("");
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [page, setPage] = useState(1);
  const confirm = useConfirm();
  const [showEditor, setShowEditor] = useState(false);
  const [search, setSearch] = useState("");
  const [baseline, setBaseline] = useState("");
  const [actionBusy, runAction] = useActionLock();
  const [clients, setClients] = useState([]);
  const [templateObject, setTemplateObject] = useState(null);
  const [templatePrompt, setTemplatePrompt] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [manualDraft, setManualDraft] = useState(createManualDraft);
  const [editorMode, setEditorMode] = useState("form");
  const [clientId, setClientId] = useState("");
  const [devis, setDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloadingId, setDownloadingId] = useState("");
  const [sendingEmailId, setSendingEmailId] = useState("");
  const [mutatingId, setMutatingId] = useState("");
  const [filter, setFilter] = useState("actifs");
  const [validationErrors, setValidationErrors] = useState([]);
  const [editingDevis, setEditingDevis] = useState(null);
  const [billingItem, setBillingItem] = useState(null);
  const [billingRows, setBillingRows] = useState([]);

  let currentContent;
  try { currentContent = editorMode === "form" ? buildContentFromDraft(manualDraft) : withoutComputedFields(JSON.parse(sanitizeJsonInput(jsonInput))); }
  catch { currentContent = jsonInput; }
  const displayedTotal = typeof currentContent === "object" && currentContent ? calculateDraftTotal(contentToDraft(currentContent)) : null;
  const snapshot = JSON.stringify({ content: currentContent, clientId });
  const dirty = showEditor && snapshot !== baseline;
  const allowNavigation = useUnsavedChanges(dirty);

  const loadPageData = async () => {
    setLoadError("");
    try {
      const [clientsResponse, devisResponse] = await Promise.all([clientsAPI.getAll(), devisAPI.getAll()]);
      setClients(clientsResponse.data || []); setDevis(devisResponse.data || []);
    } catch { setLoadError("Impossible de charger les devis et les clients. Réessayez."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    loadPageData();
    devisAPI.getTemplate().then((response) => {
      setTemplateObject(response.data?.content || null); setTemplatePrompt(response.data?.prompt || "");
      setJsonInput((current) => current || toPrettyJson(response.data?.content || createManualDraft()));
    }).catch(() => toast.error("Le gabarit est indisponible. Vous pouvez utiliser le formulaire."));
  }, []);
  const { clearDraft, draftNotice } = useRecoverableDraft("devis", { manualDraft, jsonInput, editorMode, clientId, editingDevis, baseline }, dirty,
    (draft) => { setManualDraft(draft.manualDraft); setJsonInput(draft.jsonInput); setEditorMode(draft.editorMode); setClientId(draft.clientId); setEditingDevis(draft.editingDevis); setBaseline(draft.baseline); setShowEditor(true); });

  const selectedClient = clients.find((client) => client.id === clientId);
  const manualTotal = useMemo(
    () => calculateDraftTotal(manualDraft),
    [manualDraft]
  );
  const templateText = templateObject ? toPrettyJson(templateObject) : "";
  const promptLineCount = templatePrompt ? templatePrompt.split("\n").length : 0;
  const templateLineCount = templateText ? templateText.split("\n").length : 0;

  const setDraftField = (path, value) => {
    setManualDraft((current) => {
      const next = { ...current, meta: { ...current.meta } };
      if (path[0] === "meta") {
        next.meta[path[1]] = value;
      } else {
        next[path[0]] = value;
      }
      return next;
    });
    setValidationErrors([]);
  };

  const updateCollectionItem = (collection, index, field, value) => {
    setManualDraft((current) => {
      const nextItems = [...(current[collection] || [])];
      nextItems[index] = { ...nextItems[index], [field]: value };
      if (collection === "prestations_retenues" && field === "unite" && value === "heure" && !Number(nextItems[index].prix_unitaire)) {
        nextItems[index].prix_unitaire = String(selectedClient?.taux_horaire ?? "");
      }
      return { ...current, [collection]: nextItems };
    });
    setValidationErrors([]);
  };

  const addCollectionItem = (collection, factory) => {
    setManualDraft((current) => ({
      ...current,
      [collection]: [...(current[collection] || []), factory()],
    }));
  };

  const addPresetItems = (collection, preset) => {
    const presetItems = Array.isArray(preset.items) ? preset.items : [];
    if (presetItems.length === 0) return;

    setManualDraft((current) => ({
      ...current,
      [collection]: [
        ...(current[collection] || []),
        ...presetItems.map((item) => ({ ...item })),
      ],
    }));
    setValidationErrors([]);
    toast.success(`Preset "${preset.label}" ajouté`);
  };

  const removeCollectionItem = (collection, index, fallbackFactory = null) => {
    setManualDraft((current) => {
      const nextItems = (current[collection] || []).filter((_, i) => i !== index);
      return {
        ...current,
        [collection]:
          nextItems.length > 0 || !fallbackFactory ? nextItems : [fallbackFactory()],
      };
    });
  };

  const handleCopyTemplate = async () => {
    if (!templateText) {
      toast.error("Le gabarit n'est pas encore chargé");
      return;
    }

    try {
      await navigator.clipboard.writeText(templateText);
      toast.success("Gabarit JSON copié");
    } catch (error) {
      toast.error("Impossible de copier le gabarit");
    }
  };

  const handleUseTemplate = async () => {
    if (dirty && !(await confirm({ ...discardChanges, title: "Remplacer le contenu du devis ?", description: "Le gabarit remplacera votre saisie actuelle. Les modifications non enregistrées seront perdues.", confirmLabel: "Remplacer le contenu" }))) return;
    if (!templateObject) {
      toast.error("Le gabarit n'est pas encore chargé");
      return;
    }
    const nextDraft = contentToDraft(templateObject);
    setManualDraft(nextDraft);
    setJsonInput(toPrettyJson(withoutComputedFields(templateObject)));
    setEditorMode("form");
    setValidationErrors([]);
    toast.success("Gabarit inséré dans le formulaire");
  };

  const handleCopyPrompt = async () => {
    if (!templatePrompt) {
      toast.error("Le prompt n'est pas encore chargé");
      return;
    }

    try {
      await navigator.clipboard.writeText(templatePrompt);
      toast.success("Prompt copié");
    } catch (error) {
      toast.error("Impossible de copier le prompt");
    }
  };

  const handleCopyPromptWithTemplate = async () => {
    if (!templatePrompt || !templateText) {
      toast.error("Le prompt ou le gabarit n'est pas encore chargé");
      return;
    }

    const fullPrompt = `${templatePrompt}\n\nGabarit JSON a respecter:\n${templateText}`;

    try {
      await navigator.clipboard.writeText(fullPrompt);
      toast.success("Prompt + gabarit copiés");
    } catch (error) {
      toast.error("Impossible de copier le prompt complet");
    }
  };

  const switchEditorMode = (mode) => {
    if (mode === editorMode) return;

    if (mode === "json") {
      setJsonInput(toPrettyJson(buildContentFromDraft(manualDraft)));
      setEditorMode("json");
      setValidationErrors([]);
      return;
    }

    try {
      const sanitizedInput = sanitizeJsonInput(jsonInput);
      const contenu = withoutComputedFields(JSON.parse(sanitizedInput));
      setManualDraft(contentToDraft(contenu));
      setJsonInput(toPrettyJson(contenu));
      setEditorMode("form");
      setValidationErrors([]);
    } catch (error) {
      const message = error?.message
        ? `JSON invalide: ${error.message}`
        : "JSON invalide. Impossible de repasser au formulaire.";
      setValidationErrors([message]);
      toast.error(message);
    }
  };

  const parseDraft = () => {
    if (!clientId) {
      setValidationErrors(["Sélectionnez un client."]);
      toast.error("Sélectionnez un client");
      return null;
    }

    if (editorMode === "form") {
      const contenu = buildContentFromDraft(manualDraft);
      setJsonInput(toPrettyJson(contenu));
      setValidationErrors([]);
      return { client_id: clientId, contenu };
    }

    if (!jsonInput.trim()) {
      setValidationErrors(["Collez le JSON final du devis."]);
      toast.error("Collez le JSON final du devis");
      return null;
    }

    try {
      const sanitizedInput = sanitizeJsonInput(jsonInput);
      const contenu = withoutComputedFields(JSON.parse(sanitizedInput));
      if (sanitizedInput !== jsonInput) {
        setJsonInput(toPrettyJson(contenu));
      }
      setValidationErrors([]);
      return { client_id: clientId, contenu };
    } catch (error) {
      const message = error?.message
        ? `JSON invalide: ${error.message}`
        : "JSON invalide. Vérifiez la syntaxe avant de continuer.";
      setValidationErrors([message]);
      toast.error(message);
      return null;
    }
  };

  const refreshArchive = async () => {
    const devisResponse = await devisAPI.getAll();
    setDevis(devisResponse.data || []);
  };

  const handleEdit = async (item) => {
    setSelectedDetails(null);
    setMutatingId(`edit-${item.id}`);
    try {
      const response = await devisAPI.getOne(item.id);
      const record = response.data;
      const lockReason = contentLockReason(record);
      if (lockReason) { toast.error(lockReason); return; }
      const content = withoutComputedFields(record.content || {});
      setClientId(record.client_id || "");
      setManualDraft(contentToDraft(content));
      setJsonInput(toPrettyJson(content));
      setEditorMode("form");
      setEditingDevis({ id: record.id, numero: record.numero, version: record.version || 1, signature_statut: record.signature_statut });
      setBaseline(JSON.stringify({ content: buildContentFromDraft(contentToDraft(content)), clientId: record.client_id || "" }));
      setShowEditor(true);
      setValidationErrors([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error(formatApiError(error, "Impossible de charger ce devis"));
    } finally {
      setMutatingId("");
    }
  };

  const startNew = () => {
    const draft = createManualDraft();
    setEditingDevis(null);
    setClientId("");
    setManualDraft(draft);
    setJsonInput("");
    setEditorMode("form");
    setBaseline(JSON.stringify({ content: buildContentFromDraft(draft), clientId: "" }));
    setValidationErrors([]);
    setShowEditor(true);
  };
  const handleNewVersion = (item) => runAction(async () => {
    const lockReason = contentLockReason(item);
    if (lockReason) { toast.error(lockReason); return; }
    if (!(await confirm({ title: "Créer une nouvelle version ?", description: "Le devis signé et ses preuves seront conservés. Le nouvel accord devra être signé séparément et n'annulera pas automatiquement l'accord précédent. Précisez dans le devis ce qu'il remplace ou modifie.", confirmLabel: "Créer la nouvelle version" }))) return;
    try {
      const { data } = await devisAPI.newVersion(item.id);
      await refreshArchive();
      await handleEdit(data.devis);
      toast.success("Nouvelle version créée");
    } catch (error) { toast.error(formatApiError(error, "Impossible de créer la nouvelle version")); }
  });
  const cancelEdit = async () => {
    if (generating || previewing) return;
    if (dirty && !(await confirm(discardChanges))) return;
    allowNavigation();
    setShowEditor(false);
  };

  const handleArchiveToggle = (item) => runAction(async () => {
    const isArchived = item.statut === "archive";
    const confirmed = await confirm(
      isArchived
        ? "Restaurer ce devis dans la liste active ?"
        : "Archiver ce devis sans supprimer ses fichiers ?"
    );
    if (!confirmed) return;

    setMutatingId(`${isArchived ? "restore" : "archive"}-${item.id}`);
    try {
      if (isArchived) {
        await devisAPI.restore(item.id);
        toast.success("Devis restauré");
      } else {
        await devisAPI.archive(item.id);
        toast.success("Devis archivé");
      }
      await refreshArchive();
      setValidationErrors([]);
    } catch (error) {
      toast.error(
        formatApiError(error, "Impossible de modifier le statut du devis")
      );
    } finally {
      setMutatingId("");
    }
 });

  const handleDelete = (item) => runAction(async () => {
    if (hasLinkedInvoices(item)) { toast.error("Ce devis est lié à des factures. Archivez-le pour conserver l'historique."); return; }
    const confirmed = await confirm(
      `Supprimer définitivement le devis ${item.numero} et ses fichiers associés ?`
    );
    if (!confirmed) return;

    setMutatingId(`delete-${item.id}`);
    try {
      await devisAPI.delete(item.id);
      toast.success("Devis supprimé");
      if (editingDevis?.id === item.id) {
        cancelEdit();
      }
      await refreshArchive();
      setValidationErrors([]);
    } catch (error) {
      toast.error(formatApiError(error, "Impossible de supprimer le devis"));
    } finally {
      setMutatingId("");
    }
 });

  const handleSendEmail = (item) => runAction(async () => {
    let recipient, cci;
    try {
      const [clientResponse, settings] = await Promise.all([clientsAPI.getAll(), parametresAPI.get()]);
      recipient = clientResponse.data.find((client) => client.id === item.client_id)?.email;
      cci = settings.data.copie_facture_a;
      if (!recipient) throw new Error("Renseignez l'adresse email du client avant l'envoi.");
    } catch (failure) { toast.error(failure.message || "Impossible de vérifier le destinataire"); return; }
    const confirmed = await confirm({ title: "Envoyer le devis ?", confirmLabel: "Envoyer le devis",
      description: "Le PDF du devis sera envoyé au destinataire ci-dessous.",
      details: [["Devis", item.numero], ["Client", item.client_nom], ["Destinataire", recipient], ["Copie cachée", cci], ["Montant HT", formatMoney(item.total_ht, item.devise)]] });
    if (!confirmed) return;

    setSendingEmailId(item.id);
    try {
      await devisAPI.sendEmail(item.id);
      toast.success("Devis envoyé par e-mail");
    } catch (error) {
      toast.error(formatApiError(error, "Échec de l'envoi du devis"));
    } finally {
      setSendingEmailId("");
    }
 });

  const handleGenerate = () => runAction(async () => {
    if (editingDevis && !dirty) return;
    const payload = parseDraft();
    if (!payload) return;

    const confirmed = await confirm({
      title: editingDevis ? "Enregistrer ce devis ?" : "Créer ce devis ?",
      description: editingDevis?.signature_statut === "pending" ? "L'ancien lien sera annulé. Une nouvelle version sera enregistrée, puis un nouveau lien de signature sera envoyé au client." : "Le PDF et les données du devis seront enregistrés. Aucun email ne sera envoyé par cette action.",
      confirmLabel: editingDevis ? "Enregistrer les modifications" : "Créer le devis",
      details: [["Devis", editingDevis?.numero], ["Client", selectedClient?.nom], ["Montant HT", formatMoney(calculateDraftTotal(contentToDraft(payload.contenu)), selectedClient?.devise)]],
    });
    if (!confirmed) return;

    setGenerating(true);
    try {
      if (editingDevis) {
        await devisAPI.update(editingDevis.id, { ...payload, expected_version: editingDevis.version });
        toast.success("Devis mis à jour");
        if (editingDevis.signature_statut === "pending") {
          try { await devisAPI.sendSignature(editingDevis.id); toast.success("Nouveau lien de signature envoyé"); }
          catch (error) { toast.error(formatApiError(error, "Devis enregistré, mais le nouveau lien n'a pas pu être envoyé. Renvoyez-le depuis les actions du devis.")); }
        }
      } else {
        await devisAPI.create(payload);
        toast.success("Devis créé");
      }
      clearDraft();
      setBaseline(snapshot);
      allowNavigation();
      setShowEditor(false);
      setValidationErrors([]);
      await refreshArchive().catch(() => toast.error("Devis enregistré. Actualisez la liste pour voir le document."));
    } catch (error) {
      setValidationErrors(
        extractApiErrors(error, "Erreur lors de la génération du devis")
      );
      toast.error(formatApiError(error, "Erreur lors de la génération du devis"));
    } finally {
      setGenerating(false);
    }
 });

  const handlePreview = async () => {
    const payload = parseDraft();
    if (!payload) return;

    setPreviewing(true);
    try {
      const response = await devisAPI.previewPDF(payload);
      openPDF(response, "aperçu-devis.pdf");
      setValidationErrors([]);
    } catch (error) {
      setValidationErrors(
        extractApiErrors(error, "Erreur lors de la prévisualisation du devis")
      );
      toast.error(
        formatApiError(error, "Erreur lors de la prévisualisation du devis")
      );
    } finally {
      setPreviewing(false);
    }
  };

  const handleDownload = async (devisId, type) => {
    setDownloadingId(`${type}-${devisId}`);
    try {
      const response =
        type === "pdf"
          ? await devisAPI.downloadPDF(devisId)
          : await devisAPI.downloadJSON(devisId);
      triggerBlobDownload(
        response,
        type === "pdf" ? `devis-${devisId}.pdf` : `devis-${devisId}.json`
      );
    } catch (error) {
      toast.error("Téléchargement impossible");
    } finally {
      setDownloadingId("");
    }
  };

  const openBillingModal = (item) => {
    setSelectedDetails(null);
    setBillingItem(item);
    setBillingRows(normalizeFacturationRows(item));
  };

  const closeBillingModal = () => {
    setBillingItem(null);
    setBillingRows([]);
  };

  const replaceDevisInList = (nextDevis) => {
    setDevis((current) =>
      current.map((item) => (item.id === nextDevis.id ? nextDevis : item))
    );
  };

  const handleSaveFacturationPlan = () => runAction(async () => {
    if (!billingItem) return;
    if (billingItem.statut === "archive") { toast.error("Restaurez le devis avant de modifier son échéancier."); return; }

    const cleanedRows = billingRows.map((row) => ({
      id: row.id || null,
      label: cleanText(row.label),
      pourcentage: parsePositiveNumber(row.pourcentage, 0),
    }));

    if (cleanedRows.some((row) => !row.label || row.pourcentage <= 0)) {
      toast.error("Chaque palier doit avoir un libellé et un pourcentage positif");
      return;
    }

    const total = cleanedRows.reduce((sum, row) => sum + row.pourcentage, 0);
    if (Math.abs(total - 100) > 0.01) {
      toast.error("La somme des paliers doit être égale à 100%");
      return;
    }

    setMutatingId(`plan-${billingItem.id}`);
    try {
      const response = await devisAPI.updateFacturationPlan(billingItem.id, {
        paliers: cleanedRows,
      });
      const nextDevis = response.data.devis;
      replaceDevisInList(nextDevis);
      setBillingItem(nextDevis);
      setBillingRows(normalizeFacturationRows(nextDevis));
      toast.success("Échéancier enregistré");
    } catch (error) {
      toast.error(formatApiError(error, "Impossible d'enregistrer l'échéancier"));
    } finally {
      setMutatingId("");
    }
 });

  const handleInvoicePalier = (palier) => runAction(async () => {
    if (!billingItem) return;
    if (billingItem.statut === "archive") { toast.error("Restaurez le devis avant de le facturer."); return; }

    const amount =
      (Number(billingItem.total_ht || 0) *
        parsePositiveNumber(palier.pourcentage, 0)) /
      100;
    const confirmed = await confirm(
      `Créer maintenant la facture "${palier.label}" pour ${formatCurrency(
        amount,
        billingItem.devise
      )} ?`
    );
    if (!confirmed) return;

    setMutatingId(`invoice-${billingItem.id}-${palier.id}`);
    try {
      const response = await devisAPI.invoicePalier(billingItem.id, palier.id);
      const nextDevis = response.data.devis;
      replaceDevisInList(nextDevis);
      setBillingItem(nextDevis);
      setBillingRows(normalizeFacturationRows(nextDevis));
      toast.success(`Facture ${response.data.facture?.numero || ""} créée`);
    } catch (error) {
      toast.error(formatApiError(error, "Impossible de créer la facture"));
    } finally {
      setMutatingId("");
    }
 });

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  const filteredDevis = devis.filter((item) => {
    if (params.get("client") && params.get("client") !== item.client_id) return false;
    if (!`${item.numero} ${item.client_nom} ${item.objet}`.toLocaleLowerCase("fr").includes(search.toLocaleLowerCase("fr"))) return false;
    if (filter === "tous") return true;
    if (filter === "archives") return item.statut === "archive";
    return item.statut !== "archive";
  });

  const totalPages = Math.max(1, Math.ceil(filteredDevis.length / 15));
  const currentPage = Math.min(page, totalPages);
  const visibleDevis = filteredDevis.slice((currentPage - 1) * 15, currentPage * 15);
  const detailItem = devis.find((item) => item.id === selectedDetails);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {preview}
      {draftNotice}
      <LoadError message={loadError} onRetry={loadPageData} />
      {params.get("client") && <button className="btn-secondary" onClick={() => { const next = new URLSearchParams(params); next.delete("client"); setParams(next); }}>Tous les clients</button>}
      {detailItem && <Dialog title={detailItem.numero} busy={actionBusy} onClose={() => setSelectedDetails(null)} size="max-w-2xl"><p className="mb-3 text-sm">{detailItem.client_nom} · {detailItem.objet} · {formatMoney(detailItem.total_ht, detailItem.devise)}</p>
        {hasLinkedInvoices(detailItem) && <p role="status" className="mb-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Ce devis est lié à des factures, même annulées. Son contenu et son total sont figés et sa suppression est bloquée. L'archivage conserve les factures et les paiements.</p>}
        {detailItem.statut === "archive" && <p className="mb-3 text-sm text-gray-600">Devis archivé. Restaurez-le pour reprendre la facturation des paliers restants.</p>}
        <DevisSignature item={detailItem} onUpdated={refreshArchive} onNewVersion={() => handleNewVersion(detailItem)} newVersionDisabledReason={contentLockReason(detailItem)} />{[detailItem].map((item) => <div key={item.id}>                      <div className="flex flex-wrap justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openBillingModal(item)}
                          className="btn-primary"
                        >
                          <FileText className="h-4 w-4" />
                          {item.statut === "archive" ? "Voir l'échéancier" : "Facturer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(item)}
                          disabled={actionBusy || Boolean(contentLockReason(item)) || item.signature_statut === "signed" || mutatingId === `edit-${item.id}`}
                          title={contentLockReason(item) || undefined}
                          className="btn-secondary"
                        >
                          <Edit3 className="h-4 w-4" />
                          Modifier
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(item.id, "pdf")}
                          disabled={downloadingId === `pdf-${item.id}`}
                          className="btn-secondary"
                        >
                          <Download className="h-4 w-4" />
                          PDF
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSendEmail(item)}
                          disabled={sendingEmailId === item.id}
                          className="btn-secondary"
                        >
                          <Mail className="h-4 w-4" />
                          {sendingEmailId === item.id ? "Envoi..." : "Envoyer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(item.id, "json")}
                          disabled={downloadingId === `json-${item.id}`}
                          className="btn-secondary"
                        >
                          <Download className="h-4 w-4" />
                          JSON
                        </button>
                        <button
                          type="button"
                          onClick={() => handleArchiveToggle(item)}
                          disabled={
                            mutatingId === `archive-${item.id}` ||
                            mutatingId === `restore-${item.id}`
                          }
                          className="btn-secondary"
                        >
                          {item.statut === "archive" ? (
                            <RotateCcw className="h-4 w-4" />
                          ) : (
                            <Archive className="h-4 w-4" />
                          )}
                          {item.statut === "archive" ? "Restaurer" : "Archiver"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(item)}
                          disabled={actionBusy || hasLinkedInvoices(item) || item.signature_statut === "signed" || mutatingId === `delete-${item.id}`}
                          title={hasLinkedInvoices(item) ? "Ce devis est lié à des factures. Utilisez l'archivage." : undefined}
                          className="btn-danger"
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </button>
                      </div></div>)}</Dialog>}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-gray-900">{showEditor ? editingDevis ? `Devis ${editingDevis.numero}` : "Nouveau devis" : "Devis"}</h1>
        <p className="mt-1 text-gray-600">{showEditor ? "Préparez votre proposition, puis vérifiez le PDF" : "Retrouvez vos propositions et leurs échéanciers"}</p></div>
        {showEditor ? <button className="btn-secondary" disabled={generating || previewing || actionBusy} onClick={cancelEdit}>Retour aux devis</button> : <button className="btn-primary" onClick={startNew}><Plus className="h-4 w-4" />Nouveau devis</button>}
      </div>

      {showEditor && <fieldset disabled={generating || actionBusy || previewing} className="min-w-0 flex flex-col gap-6">
        <details className="card order-2 space-y-5">
          <summary className="cursor-pointer font-medium">Outils avancés : aide IA et gabarit JSON</summary>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Aide IA et gabarit
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Le prompt reste disponible si vous voulez générer une base, mais
              le formulaire peut maintenant être rempli entièrement à la main.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={handleCopyPrompt}
              className="btn-secondary justify-start"
            >
              <Copy className="h-4 w-4" />
              Copier le prompt
            </button>
            <button
              type="button"
              onClick={handleCopyPromptWithTemplate}
              className="btn-secondary justify-start"
            >
              <Copy className="h-4 w-4" />
              Prompt + gabarit
            </button>
            <button
              type="button"
              onClick={handleCopyTemplate}
              className="btn-secondary justify-start"
            >
              <Copy className="h-4 w-4" />
              Copier le gabarit
            </button>
            <button
              type="button"
              onClick={handleUseTemplate}
              className="btn-secondary justify-start"
            >
              <FileJson className="h-4 w-4" />
              Remplir avec le gabarit
            </button>
          </div>

          <div className="space-y-4">
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-sm font-semibold text-slate-900">
                  Prompt pour l'IA
                </p>
                <span className="text-xs text-slate-500">
                  {promptLineCount} lignes
                </span>
              </div>
              <textarea
                readOnly
                className="min-h-[180px] w-full resize-y border-0 bg-white px-4 py-4 font-mono text-sm leading-6 text-slate-800 focus:outline-none focus:ring-0"
                value={templatePrompt}
              />
            </div>

            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-sm font-semibold text-slate-900">
                  Gabarit JSON
                </p>
                <span className="text-xs text-slate-500">
                  {templateLineCount} lignes
                </span>
              </div>
              <textarea
                readOnly
                className="min-h-[260px] w-full resize-y border-0 bg-white px-4 py-4 font-mono text-sm leading-6 text-slate-800 focus:outline-none focus:ring-0"
                value={templateText}
              />
            </div>
          </div>
        </details>

        <section className="card order-1 space-y-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {editingDevis
                  ? `Modifier le devis ${editingDevis.numero}`
                  : "Créer un devis"}
              </h2>
              <p className="text-sm text-gray-600">
                Remplissez les champs comme une facture, puis prévisualisez le
                PDF avant l'enregistrement.
              </p>
            </div>

          </div>

          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => switchEditorMode("form")}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${
                editorMode === "form"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Formulaire
            </button>
            <button
              type="button"
              onClick={() => switchEditorMode("json")}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium ${
                editorMode === "json"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              JSON
            </button>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="devis-53382" className="mb-1 block text-sm font-medium text-gray-700">
                Client *
              </label>
              <select id="devis-53382"
                className="input-field"
                value={clientId}
                onChange={async (event) => {
                  const nextId = event.target.value;
                  const nextClient = clients.find((client) => client.id === nextId);
                  if (selectedClient && nextClient && nextClient.devise !== selectedClient.devise && !await confirm("Changer de devise conserve les montants sans conversion. Vérifiez tous les prix avant d'émettre le devis. Continuer ?")) return;
                  setClientId(nextId);
                  setValidationErrors([]);
                }}
              >
                <option value="">Sélectionner un client</option>
                {clients.filter((client) => !client.archived || client.id === clientId).map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.nom} {client.societe ? `- ${client.societe}` : ""} (
                    {client.devise})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <BillingSummary clientId={clientId} />

          {editorMode === "form" ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label htmlFor="devis-54832" className="mb-1 block text-sm font-medium text-gray-700">
                    Objet *
                  </label>
                  <input id="devis-54832"
                    type="text"
                    className="input-field"
                    value={manualDraft.meta.objet}
                    onChange={(event) =>
                      setDraftField(["meta", "objet"], event.target.value)
                    }
                  />
                </div>
                <div>
                  <label htmlFor="devis-55326" className="mb-1 block text-sm font-medium text-gray-700">
                    Titre
                  </label>
                  <input id="devis-55326"
                    type="text"
                    className="input-field"
                    value={manualDraft.meta.titre}
                    onChange={(event) =>
                      setDraftField(["meta", "titre"], event.target.value)
                    }
                  />
                </div>
                <div>
                  <label htmlFor="devis-55818" className="mb-1 block text-sm font-medium text-gray-700">
                    Validité
                  </label>
                  <input id="devis-55818"
                    type="number"
                    min="1"
                    step="1"
                    className="input-field"
                    value={manualDraft.meta.validite_jours}
                    onChange={(event) =>
                      setDraftField(["meta", "validite_jours"], event.target.value)
                    }
                  />
                </div>
              </div>

              <div>
                <label htmlFor="devis-56408" className="mb-1 block text-sm font-medium text-gray-700">
                  Référence
                </label>
                <input id="devis-56408"
                  type="text"
                  className="input-field"
                  value={manualDraft.meta.reference}
                  onChange={(event) =>
                    setDraftField(["meta", "reference"], event.target.value)
                  }
                />
              </div>

              <div>
                <label htmlFor="devis-56887" className="mb-1 block text-sm font-medium text-gray-700">
                  Contexte *
                </label>
                <textarea id="devis-56887"
                  className="input-field min-h-[110px]"
                  value={manualDraft.contexte}
                  onChange={(event) =>
                    setDraftField(["contexte"], event.target.value)
                  }
                />
              </div>

              <div>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <h3 className="font-semibold text-gray-900">
                    Prestations retenues
                  </h3>
                  <button
                    type="button"
                    onClick={() =>
                      addCollectionItem("prestations_retenues", createDevisLine)
                    }
                    className="btn-secondary"
                  >
                    <Plus className="h-4 w-4" />
                    Ajouter une ligne
                  </button>
                </div>

                {manualDraft.prestations_retenues.map((line, index) => (
                  <div
                    key={index}
                    className="mb-4 rounded-lg border border-gray-200 p-4"
                  >
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <h4 className="font-medium text-gray-900">
                        Prestation {index + 1}
                      </h4>
                      <button
                        type="button"
                        aria-label={`Supprimer la prestation ${index + 1}`}
                        onClick={() =>
                          removeCollectionItem(
                            "prestations_retenues",
                            index,
                            createDevisLine
                          )
                        }
                        className="rounded p-2 text-red-600 hover:bg-red-50 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
                      <div className="md:col-span-3">
                        <label htmlFor={`devis-59210-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                          Description *
                        </label>
                        <input id={`devis-59210-${index}`}
                          type="text"
                          className="input-field"
                          value={line.description}
                          onChange={(event) =>
                            updateCollectionItem(
                              "prestations_retenues",
                              index,
                              "description",
                              event.target.value
                            )
                          }
                        />
                      </div>
                      <div>
                        <label htmlFor={`devis-59966-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                          Unité
                        </label>
                        <select id={`devis-59966-${index}`}
                          className="input-field"
                          value={line.unite}
                          onChange={(event) =>
                            updateCollectionItem(
                              "prestations_retenues",
                              index,
                              "unite",
                              event.target.value
                            )
                          }
                        >
                          <option value="forfait">Forfait</option>
                          <option value="heure">Heure</option>
                          <option value="jour">Jour</option>
                          <option value="mois">Mois</option>
                          <option value="unite">Unité</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`devis-61013-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                          Quantité *
                        </label>
                        <input id={`devis-61013-${index}`}
                          type="number"
                          min="0"
                          step="0.01"
                          className="input-field"
                          value={line.quantite}
                          onChange={(event) =>
                            updateCollectionItem(
                              "prestations_retenues",
                              index,
                              "quantite",
                              event.target.value
                            )
                          }
                        />
                      </div>
                      <div>
                        <label htmlFor={`devis-61834-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                          Type prix
                        </label>
                        <select id={`devis-61834-${index}`}
                          className="input-field"
                          value={line.price_type}
                          onChange={(event) =>
                            updateCollectionItem(
                              "prestations_retenues",
                              index,
                              "price_type",
                              event.target.value
                            )
                          }
                        >
                          <option value="fixed">Chiffré</option>
                          <option value="free">Libre</option>
                        </select>
                      </div>

                      {line.price_type === "free" ? (
                        <div className="md:col-span-3">
                          <label htmlFor={`devis-62792-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                            Prix libre *
                          </label>
                          <input id={`devis-62792-${index}`}
                            type="text"
                            className="input-field"
                            placeholder="Sur devis, inclus, par avenant..."
                            value={line.prix_libre}
                            onChange={(event) =>
                              updateCollectionItem(
                                "prestations_retenues",
                                index,
                                "prix_libre",
                                event.target.value
                              )
                            }
                          />
                        </div>
                      ) : (
                        <>
                          <div className="md:col-span-2">
                            <label htmlFor={`devis-63742-${index}`} className="mb-1 block text-sm font-medium text-gray-700">
                              Prix unitaire *
                            </label>
                            <input id={`devis-63742-${index}`}
                              type="number"
                              min="0"
                              step="0.01"
                              className="input-field"
                              value={line.prix_unitaire}
                              onChange={(event) =>
                                updateCollectionItem(
                                  "prestations_retenues",
                                  index,
                                  "prix_unitaire",
                                  event.target.value
                                )
                              }
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Total
                            </label>
                            <div className="input-field bg-gray-50 text-right font-medium">
                              {formatMoney(parsePositiveNumber(line.quantite, 0) * parsePositiveNumber(line.prix_unitaire, 0), selectedClient?.devise)}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <span className="text-lg font-medium text-gray-900">
                      Total HT :
                    </span>
                    <span className="text-2xl font-bold text-primary-600">
                      {formatMoney(manualTotal, selectedClient?.devise)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <EditableMiniList
                  title="Options et variantes"
                  items={manualDraft.options_variantes}
                  addLabel="Ajouter une option"
                  onAdd={() => addCollectionItem("options_variantes", createOption)}
                  onRemove={(index) =>
                    removeCollectionItem("options_variantes", index)
                  }
                  fields={[
                    ["description", "Description"],
                    ["type_element", "Type"],
                    ["tarif", "Tarif"],
                  ]}
                  onChange={(index, field, value) =>
                    updateCollectionItem("options_variantes", index, field, value)
                  }
                />
                <EditableMiniList
                  title="Totaux indicatifs"
                  items={manualDraft.totaux_indicatifs}
                  addLabel="Ajouter un total"
                  onAdd={() =>
                    addCollectionItem("totaux_indicatifs", createTotalIndicatif)
                  }
                  onRemove={(index) =>
                    removeCollectionItem("totaux_indicatifs", index)
                  }
                  fields={[
                    ["libelle", "Libellé"],
                    ["montant", "Montant"],
                  ]}
                  onChange={(index, field, value) =>
                    updateCollectionItem("totaux_indicatifs", index, field, value)
                  }
                />
                <EditableMiniList
                  title="Abonnement mensuel"
                  items={manualDraft.abonnement_mensuel}
                  addLabel="Ajouter un abonnement"
                  presets={ABONNEMENT_PRESETS}
                  onApplyPreset={(preset) =>
                    addPresetItems("abonnement_mensuel", preset)
                  }
                  onAdd={() =>
                    addCollectionItem("abonnement_mensuel", createAbonnement)
                  }
                  onRemove={(index) =>
                    removeCollectionItem("abonnement_mensuel", index)
                  }
                  fields={[
                    ["description", "Description"],
                    ["tarif", "Tarif"],
                  ]}
                  onChange={(index, field, value) =>
                    updateCollectionItem("abonnement_mensuel", index, field, value)
                  }
                />
                <EditableMiniList
                  title="Conditions"
                  items={manualDraft.conditions}
                  addLabel="Ajouter une condition"
                  presets={CONDITION_PRESETS}
                  onApplyPreset={(preset) => addPresetItems("conditions", preset)}
                  onAdd={() => addCollectionItem("conditions", createCondition)}
                  onRemove={(index) => removeCollectionItem("conditions", index)}
                  fields={[
                    ["titre", "Titre"],
                    ["contenu", "Contenu"],
                  ]}
                  multilineFields={["contenu"]}
                  onChange={(index, field, value) =>
                    updateCollectionItem("conditions", index, field, value)
                  }
                />
              </div>

              <div>
                <label htmlFor="devis-69198" className="mb-1 block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <textarea id="devis-69198"
                  className="input-field min-h-[90px]"
                  value={manualDraft.notes}
                  onChange={(event) => setDraftField(["notes"], event.target.value)}
                />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="devis-69629" className="mb-1 block text-sm font-medium text-gray-700">
                JSON final *
              </label>
              <textarea id="devis-69629"
                className="input-field min-h-[620px] resize-y font-mono text-sm"
                placeholder="Collez ici le JSON final du devis..."
                value={jsonInput}
                onChange={(event) => {
                  setJsonInput(event.target.value);
                  setValidationErrors([]);
                }}
              />
            </div>
          )}

          {validationErrors.length > 0 ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-800">
                Devis refusé par la validation
              </p>
              <ul className="mt-2 space-y-1 text-sm text-red-700">
                {validationErrors.map((message, index) => (
                  <li key={`${message}-${index}`}>- {message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="sticky bottom-0 z-20 flex flex-col gap-3 rounded-xl border bg-white/95 p-4 shadow-lg sm:flex-row sm:items-center sm:justify-end">
            <p className="mr-auto text-sm font-semibold">Total HT : {displayedTotal === null ? "JSON à vérifier" : formatMoney(displayedTotal, selectedClient?.devise)}</p>
            <button
              type="button"
              onClick={handlePreview}
              disabled={generating || previewing || actionBusy}
              className="btn-secondary"
            >
              {previewing ? (
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-gray-700" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
              {previewing ? "Prévisualisation..." : "Prévisualiser le PDF"}
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating || previewing || actionBusy || (editingDevis && !dirty)}
              className="btn-primary"
            >
              {generating ? (
                <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {generating
                ? "Enregistrement..."
                : editingDevis
                ? "Enregistrer les modifications"
                : "Créer le devis"}
            </button>
          </div>
        </section>
      </fieldset>}

      {!showEditor && <section className="card">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Vos devis
            </h2>
            <p className="text-sm text-gray-600">
              Chaque devis conserve son PDF et son JSON source.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFilter("actifs")}
              className={`btn-secondary ${filter === "actifs" ? "ring-2 ring-primary-500" : ""}`}
            >
              Actifs
            </button>
            <button
              type="button"
              onClick={() => setFilter("archives")}
              className={`btn-secondary ${filter === "archives" ? "ring-2 ring-primary-500" : ""}`}
            >
              Archivés
            </button>
            <button
              type="button"
              onClick={() => setFilter("tous")}
              className={`btn-secondary ${filter === "tous" ? "ring-2 ring-primary-500" : ""}`}
            >
              Tous
            </button>
          </div>
        </div>

        <label className="mt-4 block text-sm">Rechercher un devis<input className="input-field mt-1" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Numéro, client ou objet" /></label>
        {!desktop && <div className="space-y-3">{visibleDevis.map((item) => <article key={item.id} className="mt-4 rounded-lg border p-4"><button className="font-semibold text-primary-700" onClick={() => setSelectedDetails(item.id)}>{item.numero}</button><p className="text-sm">{item.client_nom}</p><p className="mt-2 text-sm">{item.objet}</p><p className="mt-2 text-xs text-gray-500">{item.statut === "archive" ? "Archivé" : "Actif"} · {formatDateTime(item.date_creation)}</p><div className="mt-3 flex items-center justify-between gap-2"><span>{formatMoney(item.total_ht, item.devise)}</span><button className="btn-secondary" onClick={() => setSelectedDetails(item.id)}>Actions</button></div></article>)}</div>}
        {totalPages > 1 && <nav aria-label="Pagination des devis" className="mt-3 flex items-center gap-3"><button className="btn-secondary" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Précédent</button><span>{currentPage} / {totalPages}</span><button className="btn-secondary" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>Suivant</button></nav>}
        {filteredDevis.length === 0 && !loadError ? (
          <div className="mt-4 rounded-lg border border-dashed border-gray-300 p-6 text-sm text-gray-500">
            Aucun devis dans ce filtre.
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            {desktop && <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    N°
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Objet
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Total HT
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleDevis.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900">
                      <button className="text-primary-700 hover:underline" onClick={() => setSelectedDetails(item.id)}>{item.numero}</button>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {item.client_nom}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <div className="font-medium text-gray-900">{item.objet}</div>
                      {item.reference ? (
                        <div className="mt-1 text-xs text-gray-500">
                          Réf. {item.reference}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      {formatDateTime(item.date_creation)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <span
                        className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                          item.statut === "archive"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {item.statut === "archive" ? "Archivé" : "Actif"}
                      </span>
                      <div className="mt-1 text-xs text-gray-600">V{item.version || 1} · {signatureLabel(item.signature_statut)}</div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">
                      <div className="font-medium text-gray-900">
                        {formatCurrency(item.total_ht, item.devise)}
                      </div>
                      {item.facturation_statut !== "non_facture" ? (
                        <div className="mt-1 text-xs text-gray-500">
                          Facturé {formatCurrency(item.montant_facture, item.devise)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4">
                      <button className="btn-secondary" onClick={() => setSelectedDetails(item.id)}>Actions</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>}
          </div>
        )}
      </section>}

      {billingItem ? (
        <FacturationPlanModal
          item={billingItem}
          rows={billingRows}
          mutatingId={mutatingId}
          onChangeRows={setBillingRows}
          onClose={closeBillingModal}
          onSavePlan={handleSaveFacturationPlan}
          onInvoicePalier={handleInvoicePalier}
        />
      ) : null}
    </div>
  );
};

const EditableMiniList = ({
  title,
  items,
  fields,
  multilineFields = [],
  addLabel,
  presets = [],
  onAdd,
  onRemove,
  onChange,
  onApplyPreset,
}) => (
  <details className="rounded-lg border border-gray-200 p-4">
    <summary className="cursor-pointer font-semibold">{title} ({items.length})</summary>
    <div className="mt-4">
    <div className="mb-3 flex items-center justify-between gap-3">
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <button
        type="button"
        onClick={onAdd}
        className="btn-secondary min-h-[38px] px-3 py-1 text-sm"
      >
        <Plus className="h-4 w-4" />
        {addLabel}
      </button>
    </div>

    {presets.length > 0 ? (
      <div className="mb-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
          Presets
        </p>
        <div className="flex flex-wrap gap-2">
          {presets.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => onApplyPreset?.(preset)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:border-primary-300 hover:text-primary-700"
            >
              <Plus className="mr-1 inline h-3.5 w-3.5" />
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    ) : null}

    {items.length === 0 ? (
      <p className="rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-500">
        Aucune ligne.
      </p>
    ) : (
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={index} className="rounded-lg bg-gray-50 p-3">
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                aria-label={`Supprimer la ligne ${index + 1} de ${title}`}
                onClick={() => onRemove(index)}
                className="rounded p-1.5 text-red-600 hover:bg-red-50 hover:text-red-900"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {fields.map(([field, label]) => (
                <div key={field}>
                  <label htmlFor={`devis-${title.replace(/\s+/g, "-")}-${index}-${field}`} className="mb-1 block text-sm font-medium text-gray-700">
                    {label}
                  </label>
                  {multilineFields.includes(field) ? (
                    <textarea id={`devis-${title.replace(/\s+/g, "-")}-${index}-${field}`}
                      className="input-field min-h-[76px]"
                      value={item[field] || ""}
                      onChange={(event) => onChange(index, field, event.target.value)}
                    />
                  ) : (
                    <input id={`devis-${title.replace(/\s+/g, "-")}-${index}-${field}`}
                      type="text"
                      className="input-field"
                      value={item[field] || ""}
                      onChange={(event) => onChange(index, field, event.target.value)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}
    </div>
  </details>
);

export default Devis;
